import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useMediaQuery } from "react-responsive";
import Cookies from "js-cookie";
import API_ENDPOINTS from "../config/api";

// 고유 ID 생성 함수
const encodeUUID = () => {
  const randomPart = Math.random().toString(36).substring(2, 10);
  const timestampPart = Date.now().toString(36);
  return `${randomPart}${timestampPart}`;
};

const PaymentMobilePage = () => {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery({ minWidth: 768 });
  const [isChecked, setIsChecked] = useState(false);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const lawsuitAmount = parseInt(Cookies.get("lawsuit_amount") || "5000", 10);
  const formRef = useRef(null);

  // 사용자 정보 가져오기
  useEffect(() => {
    const fetchUserData = async () => {
      setIsLoading(true);
      try {
        const token = Cookies.get(
          API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN
        );

        if (!token) {
          throw new Error("인증 토큰이 없습니다. 다시 로그인해주세요.");
        }

        const response = await fetch(`${API_ENDPOINTS.BASE_URL}/clients/me`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          throw new Error("사용자 정보를 가져오는데 실패했습니다.");
        }

        const data = await response.json();
        setUserData(data);
      } catch (err) {
        setError(err.message || "사용자 정보를 가져오는데 실패했습니다.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, []);

  // 뒤로 가기
  const handleBack = () => {
    navigate(-1);
  };

  // 결제 완료 API 호출
  const completePayment = async (paymentId) => {
    try {
      const token = Cookies.get(API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN);
      if (!token) {
        throw new Error("인증 토큰이 없습니다.");
      }

      const applicationNumber = Cookies.get("application_number");
      if (!applicationNumber) {
        throw new Error("신청번호가 없습니다.");
      }

      const response = await fetch(
        `${API_ENDPOINTS.BASE_URL}/payments/complete`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            paymentId: paymentId,
            applicationNumber: applicationNumber,
            price: lawsuitAmount,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "결제 완료 처리에 실패했습니다.");
      }

      return await response.json();
    } catch (error) {
      // 결제 취소 시도
      const cancelSuccess = await cancelPayment(paymentId);

      if (cancelSuccess) {
      } else {
        Object.keys(Cookies.get()).forEach((cookie) => {
          Cookies.remove(cookie);
        });
        // 결제는 성공했으나 취소 실패했으므로 완료 페이지로 이동
        navigate("/complete");
      }
      throw error;
    }
  };

  // 결제 취소 API 호출
  const cancelPayment = async (
    paymentId,
    reason = "결제 완료 처리 실패로 인한 취소"
  ) => {
    try {
      const token = Cookies.get(API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN);
      if (!token) {
        throw new Error("인증 토큰이 없습니다.");
      }

      const response = await fetch(
        `${API_ENDPOINTS.BASE_URL}/payments/cancel`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            paymentId: paymentId,
            reason: reason,
          }),
        }
      );

      if (!response.ok) {
        return false;
      }

      return true;
    } catch (error) {
      return false;
    }
  };

  // 애니메이션 변수
  const pageVariants = {
    initial: {
      opacity: 0,
    },
    in: {
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
    out: {
      opacity: 0,
      transition: {
        duration: 0.3,
      },
    },
  };

  const contentVariants = {
    initial: {
      opacity: 0,
      y: 20,
    },
    in: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: 0.2,
        staggerChildren: 0.1,
      },
    },
  };

  const processPayment = async () => {
    if (!isChecked) {
      return;
    }

    if (!userData) {
      return;
    }

    try {
      setIsPaymentProcessing(true);

      // 주문번호 생성 (반드시 유니크 값)
      const orderId = `order_${encodeUUID()}`;

      // 폼 제출
      if (formRef.current) {
        formRef.current.submit();
      }
    } catch (error) {
      setIsPaymentProcessing(false);
    }
  };

  // 로딩 표시
  if (isLoading) {
    return (
      <PageBackground>
        <Container>
          <Header>
            <BackButton onClick={handleBack}>
              <BackIcon>&#10094;</BackIcon>
            </BackButton>
            <HeaderTitle>휴대폰 결제</HeaderTitle>
          </Header>
          <Content>
            <LoadingContainer>
              <Spinner />
              <LoadingMessage>
                사용자 정보를 불러오는 중입니다...
              </LoadingMessage>
            </LoadingContainer>
          </Content>
        </Container>
      </PageBackground>
    );
  }

  // 에러 표시
  if (error) {
    return (
      <PageBackground>
        <Container>
          <Header>
            <BackButton onClick={handleBack}>
              <BackIcon>&#10094;</BackIcon>
            </BackButton>
            <HeaderTitle>휴대폰 결제</HeaderTitle>
          </Header>
          <Content>
            <ErrorContainer>
              <ErrorIcon>⚠️</ErrorIcon>
              <ErrorTitle>오류가 발생했습니다</ErrorTitle>
              <ErrorDescription>{error}</ErrorDescription>
              <RetryButton onClick={() => window.location.reload()}>
                다시 시도하기
              </RetryButton>
            </ErrorContainer>
          </Content>
        </Container>
      </PageBackground>
    );
  }

  // 이니시스 모바일 환경에 따른 URL 결정
  const paymentDomain =
    process.env.NODE_ENV === "production"
      ? "https://mobile.inicis.com"
      : "https://stgmobile.inicis.com";

  // 가맹점 ID (테스트 환경에서는 테스트용 ID 사용)
  const merchantId =
    process.env.NODE_ENV === "production"
      ? "YOUR_PRODUCTION_MID" // 실제 운영 시 발급받은 MID로 변경 필요
      : "INIpayTest";

  // 주문번호 생성 (반드시 유니크 값)
  const orderId = `order_${encodeUUID()}`;

  // 결과 수신 URL (배포 환경에 맞게 설정)
  const nextUrl =
    process.env.NODE_ENV === "production"
      ? "https://www.lawfirmthe-h-skt.info/payment/mobile/result"
      : "http://localhost:3000/payment/mobile/result";

  // 가상계좌 입금통보 URL (필요 시 설정)
  const notiUrl =
    process.env.NODE_ENV === "production"
      ? "https://www.lawfirmthe-h-skt.info/api/payments/vbank-notification"
      : "";

  return (
    <PageBackground>
      <Container
        variants={pageVariants}
        initial="initial"
        animate="in"
        exit="out"
      >
        <Header>
          <BackButton onClick={handleBack}>
            <BackIcon>&#10094;</BackIcon>
          </BackButton>
          <HeaderTitle>휴대폰 결제</HeaderTitle>
        </Header>
        <Content variants={contentVariants} initial="initial" animate="in">
          <ContentSection>
            <PageHeader>휴대폰 소액결제</PageHeader>
            <TitleWithIcon>
              <PayIcon src="/png/banktransferIcon.png" alt="휴대폰 아이콘" />
              <SectionTitle>결제하기</SectionTitle>
            </TitleWithIcon>
            <SectionSubtitle>
              소송 접수를 위한 비용을 휴대폰으로 결제해주세요
            </SectionSubtitle>

            {userData && (
              <UserInfoBox>
                <BoxTitle>결제자 정보</BoxTitle>
                <DetailRow>
                  <DetailLabel>이름</DetailLabel>
                  <DetailValue>{userData.name}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>연락처</DetailLabel>
                  <DetailValue>{userData.phoneNumber}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>이메일</DetailLabel>
                  <DetailValue>{userData.email}</DetailValue>
                </DetailRow>
              </UserInfoBox>
            )}

            <PaymentDetailsBox>
              <BoxTitle>결제 금액 상세</BoxTitle>
              <DetailRow>
                <DetailLabel>접수비</DetailLabel>
                <DetailValue>{lawsuitAmount.toLocaleString()}원</DetailValue>
              </DetailRow>
              <Divider />
              <TotalRow>
                <TotalLabel>총 결제 금액</TotalLabel>
                <TotalValue>{lawsuitAmount.toLocaleString()}원</TotalValue>
              </TotalRow>
            </PaymentDetailsBox>

            <MobilePayInfoBox>
              <BoxTitle>휴대폰 결제 안내</BoxTitle>
              <MobileInfoItem>
                • 휴대폰 소액결제는 통신사 요금과 함께 청구됩니다.
              </MobileInfoItem>
              <MobileInfoItem>
                • 결제 시 본인 명의의 휴대폰으로만 결제가 가능합니다.
              </MobileInfoItem>
              <MobileInfoItem>• 모든 통신사 이용 가능합니다.</MobileInfoItem>
              <MobileInfoItem>
                • 월 결제 한도는 통신사별로 다를 수 있습니다.
              </MobileInfoItem>
            </MobilePayInfoBox>

            <TermsSection>
              <TermsTitle>환불 규정 및 주의사항</TermsTitle>
              <TermsList>
                <TermsItem>
                  • 착수금 또는 관리비는 소송 진행을 위해 필요한 비용입니다.
                </TermsItem>
                <TermsItem>
                  • 납부 후 취소 시 접수비는 환불되지 않습니다.
                </TermsItem>
                <TermsItem>
                  • 소송 접수 후 취소 시 송달료는 실비 정산 후 환불됩니다.
                </TermsItem>
                <TermsItem>
                  • 소송 진행 중 항소 단계에서만 추가 비용이 발생할 수 있으며,
                  패소에 따른 별도 비용은 없습니다. 추가 비용이 생길 경우 사전에
                  안내해 드립니다.
                </TermsItem>
              </TermsList>

              <CheckboxContainer onClick={() => setIsChecked(!isChecked)}>
                <Checkbox checked={isChecked}>
                  {isChecked ? (
                    <CheckedImg src="/png/checkedYellow.png" alt="체크박스" />
                  ) : (
                    <CheckedImg src="/png/checkedinActive.png" alt="체크박스" />
                  )}
                </Checkbox>
                <CheckboxLabel>
                  상기 내용을 모두 확인하였으며 동의합니다.
                </CheckboxLabel>
              </CheckboxContainer>
            </TermsSection>

            <ButtonContainer>
              <PaymentButton
                onClick={processPayment}
                disabled={!isChecked || isPaymentProcessing}
              >
                {isPaymentProcessing
                  ? "결제 처리 중..."
                  : "휴대폰으로 결제하기"}
              </PaymentButton>
            </ButtonContainer>

            {/* 이니시스 결제 폼 - 숨겨진 상태로 제출됨 */}
            <HiddenForm
              ref={formRef}
              method="post"
              acceptCharset="EUC-KR"
              action={`${paymentDomain}/smart/payment/`}
            >
              <input type="hidden" name="P_INI_PAYMENT" value="MOBILE" />
              <input type="hidden" name="P_MID" value={merchantId} />
              <input type="hidden" name="P_OID" value={orderId} />
              <input type="hidden" name="P_AMT" value={lawsuitAmount} />
              <input
                type="hidden"
                name="P_GOODS"
                value="쿠팡 집단소송 접수비"
              />
              <input
                type="hidden"
                name="P_UNAME"
                value={userData?.name || ""}
              />
              <input type="hidden" name="P_NEXT_URL" value={nextUrl} />
              {notiUrl && (
                <input type="hidden" name="P_NOTI_URL" value={notiUrl} />
              )}
              <input type="hidden" name="P_HPP_METHOD" value="1" />{" "}
              {/* 1: 컨텐츠, 2: 실물 */}
              <input type="hidden" name="P_RESERVED" value="centerCd=Y" />{" "}
              {/* IDC센터코드 수신 사용옵션 */}
              {/* 모바일 앱에서 결제 시 필요한 앱 스키마 설정 */}
              {isDesktop === false && (
                <input
                  type="hidden"
                  name="P_RESERVED"
                  value="centerCd=Y&iosapp=Y&app_scheme=sktlawsuit://"
                />
              )}
            </HiddenForm>

            <div style={{ height: "80px" }} />
          </ContentSection>
        </Content>
      </Container>
    </PageBackground>
  );
};

// 스타일 컴포넌트
const PageBackground = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 0;
  background-color: #f8f8f8;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (min-width: 769px) {
    padding: 40px 20px 60px;
    background-color: #fff;
  }
`;

const Container = styled(motion.div)`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-color: #f8f8f8;

  /* 모바일 디바이스 */
  @media (max-width: 768px) {
    min-height: auto;
    box-shadow: none;
    padding: 0 0 80px 0;
    max-width: 100%;
    margin: 0;
    border-radius: 0;
  }

  /* 데스크탑 디바이스 */
  @media (min-width: 769px) {
    border-radius: 20px;
    min-height: auto;
    max-height: none;
    max-width: 470px;
    margin: 0 auto;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
    padding: 20px;
  }
`;

const HiddenForm = styled.form`
  display: none;
`;

const Header = styled.div`
  width: 100%;
  padding: 16px 0;
  text-align: center;
  position: relative;
  border-bottom: 1px solid #eee;
`;

const BackButton = styled.button`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BackIcon = styled.span`
  font-size: 20px;
  color: #333;
`;

const HeaderTitle = styled.h1`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const Content = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 20px;
  box-sizing: border-box;
`;

const ContentSection = styled.div`
  width: 100%;
  max-width: 500px;
`;

const PageHeader = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #333;
  margin: 0 0 30px 0;
  display: none;
`;

const TitleWithIcon = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  margin-top: 10px;
`;

const PayIcon = styled.img`
  width: 32px;
  height: 32px;
  margin-right: 10px;
`;

const SectionTitle = styled.h2`
  font-size: 22px;
  font-weight: 700;
  color: #333;
  margin: 0;
`;

const SectionSubtitle = styled.p`
  font-size: 16px;
  color: #666;
  margin: 8px 0 24px 0;
`;

const UserInfoBox = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
`;

const BoxTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 20px 0;
  color: #333;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
`;

const DetailLabel = styled.span`
  color: #666;
`;

const DetailValue = styled.span`
  font-weight: 500;
  color: #333;
`;

const PaymentDetailsBox = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
`;

const MobilePayInfoBox = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
`;

const MobileInfoItem = styled.p`
  font-size: 14px;
  color: #666;
  margin: 8px 0;
  line-height: 1.5;
`;

const Divider = styled.div`
  height: 1px;
  background-color: #eee;
  margin: 15px 0;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const TotalLabel = styled.span`
  font-weight: 600;
  color: #333;
`;

const TotalValue = styled.span`
  font-weight: 700;
  font-size: 18px;
  color: #333;
`;

const TermsSection = styled.div`
  margin-bottom: 24px;
`;

const TermsTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 15px 0;
  color: #333;
`;

const TermsList = styled.div`
  background-color: #f9f9f9;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 15px;
`;

const TermsItem = styled.p`
  font-size: 14px;
  color: #555;
  margin: 8px 0;
  line-height: 1.5;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 8px 0;
`;

const Checkbox = styled.div`
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const CheckedImg = styled.img`
  max-width: 100%;
  max-height: 100%;
`;

const CheckboxLabel = styled.span`
  font-size: 14px;
  margin-left: 10px;
  color: #333;
  font-weight: 500;
`;

const ButtonContainer = styled.div`
  width: 100%;
  margin-top: 20px;
`;

const PaymentButton = styled.button`
  display: flex;
  width: 100%;
  height: 56px;
  padding: 0px 14px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  border-radius: 8px;
  background: ${(props) => (props.disabled ? "#ccc" : "#0075ff")};
  color: white;
  font-size: 16px;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  border: none;
  opacity: ${(props) => (props.disabled ? 0.7 : 1)};
  transition: background-color 0.2s;
`;

// 로딩 관련 컴포넌트
const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  width: 100%;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid rgba(0, 117, 255, 0.1);
  border-radius: 50%;
  border-left-color: #0075ff;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingMessage = styled.p`
  font-size: 16px;
  color: #666;
  text-align: center;
`;

// 에러 관련 컴포넌트
const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  width: 100%;
  text-align: center;
`;

const ErrorIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

const ErrorTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0 0 8px 0;
`;

const ErrorDescription = styled.p`
  font-size: 16px;
  color: #666;
  margin: 0 0 24px 0;
`;

const RetryButton = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  background-color: #0075ff;
  color: white;
  font-size: 16px;
  font-weight: 600;
  border: none;
  cursor: pointer;
`;

export default PaymentMobilePage;
